import { useEffect, useRef, useState } from 'react';
import './HelpPanel.css';

/**
 * "How this works" — the onboarding drawer every screen in this product carries.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/partials/help-panel.ejs`,
 * including its own comment on what the shape is for:
 *
 *   Reusable "How this works" onboarding component.
 *   Single-panel:  include with a `help` object.
 *   Tabbed panels: include with a `helps` array of such objects (each may add `tabLabel`);
 *                  the drawer shows a tab switch, one pane per entry.
 *
 * A SHARED COMPONENT, deliberately. CLAUDE.md §1.2.1 forbids collapsing several source
 * screens into one parameterised component — but this IS one source component, a partial
 * included by every screen with its own help object. Copying it per screen would be the
 * deviation, not the reuse.
 *
 * ESCAPE CLOSES IT, as the source's global keydown handler does.
 *
 * `dangerouslySetInnerHTML` IS THE RIGHT CALL HERE and nowhere else on these screens: the
 * source writes its help copy with `<strong>`, `<em>` and `&mdash;` inline and renders it
 * with EJS's raw `<%- %>`. The content is a hard-coded constant in the component that
 * declares it — never user input, never anything off the wire — so there is nothing for a
 * user to inject. Rendering it as text would print the tags.
 */

export interface HelpDef {
  term: string;
  color?: string;
  def: string;
}

export interface HelpSection {
  label?: string;
  type: 'defs' | 'html';
  items?: HelpDef[];
  note?: string;
  html?: string;
}

export interface HelpContent {
  tabLabel?: string;
  title: string;
  icon?: string;
  purpose?: string;
  doHere?: string[];
  fits?: string;
  firstTime?: string;
  sections?: HelpSection[];
  links?: { label: string; href: string }[];
}

export interface HelpPanelProps {
  /** One panel. */
  help?: HelpContent;
  /** Several panels behind a tab switch — the source's `helps` array. */
  helps?: HelpContent[];
  /** Which pane to show, when a screen's own tabs drive the panel (`hlpShow`). */
  activeIndex?: number;
  /**
   * Bump this to open the drawer from somewhere else on the screen.
   *
   * Delivery Pathway needs it: its guardrails belong in the drawer (as the source has
   * them), but the owner wants the way in to sit beside "Project & opportunities" rather
   * than only in the fixed top-right launcher. Optional and ignored by every other screen,
   * so this stays additive.
   */
  openSignal?: number;
}

const raw = (html: string) => ({ __html: html });

export function HelpPanel({ help, helps, activeIndex, openSignal }: HelpPanelProps) {
  const panes = helps && helps.length ? helps : [help ?? ({ title: 'How this works' } as HelpContent)];
  const multi = panes.length > 1;

  const [open, setOpen] = useState(false);
  const [pane, setPane] = useState(0);

  /* A screen with its own tabs can drive the panel — the source's `hlpShow(i)`. */
  useEffect(() => {
    if (activeIndex != null) setPane(Math.min(Math.max(activeIndex, 0), panes.length - 1));
  }, [activeIndex, panes.length]);

  /* An external opener. Ignores the first render so the drawer does not open on mount. */
  const seenSignal = useRef(openSignal);
  useEffect(() => {
    if (openSignal === undefined || openSignal === seenSignal.current) return;
    seenSignal.current = openSignal;
    setOpen(true);
  }, [openSignal]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const h = panes[pane] ?? panes[0]!;

  return (
    <>
      <button
        type="button"
        className="hlp-launch"
        onClick={() => setOpen((o) => !o)}
        title="How this works"
      >
        <span className="hlp-q">?</span> How this works
      </button>

      <div className={`hlp-overlay${open ? ' on' : ''}`} onClick={() => setOpen(false)} />
      <aside className={`hlp-drawer${open ? ' on' : ''}`} role="dialog" aria-label="How this works">
        <div className="hlp-head">
          <div className="hlp-title">
            <span className="hlp-ic">{h.icon || 'ℹ️'}</span>
            <span>{h.title || 'How this works'}</span>
          </div>
          <button className="hlp-x" onClick={() => setOpen(false)} aria-label="Close">
            ×
          </button>
        </div>

        {multi ? (
          <div className="hlp-tabs" role="tablist">
            {panes.map((p, i) => (
              <button
                type="button"
                key={p.title}
                className={`hlp-tab${i === pane ? ' on' : ''}`}
                onClick={() => setPane(i)}
              >
                {p.tabLabel || p.title}
              </button>
            ))}
          </div>
        ) : null}

        <div className="hlp-body">
          {h.purpose ? <p className="hlp-purpose" dangerouslySetInnerHTML={raw(h.purpose)} /> : null}

          {h.doHere?.length ? (
            <>
              <div className="hlp-lbl">What you do here</div>
              <ul className="hlp-list">
                {h.doHere.map((d, i) => (
                  <li key={i} dangerouslySetInnerHTML={raw(d)} />
                ))}
              </ul>
            </>
          ) : null}

          {h.fits ? (
            <>
              <div className="hlp-lbl">Where it fits</div>
              <div className="hlp-fits" dangerouslySetInnerHTML={raw(h.fits)} />
            </>
          ) : null}

          {h.firstTime ? (
            <div className="hlp-first">
              <span className="hlp-first-ic">▶</span>
              <div>
                <div className="hlp-first-lbl">First time? Do this</div>
                <div className="hlp-first-txt" dangerouslySetInnerHTML={raw(h.firstTime)} />
              </div>
            </div>
          ) : null}

          {(h.sections ?? []).map((sec, i) => (
            <div key={i}>
              {sec.label ? <div className="hlp-lbl">{sec.label}</div> : null}
              {sec.type === 'defs' ? (
                <>
                  <div className="hlp-defs">
                    {(sec.items ?? []).map((it) => (
                      <div className="hlp-def" key={it.term}>
                        <span
                          className="hlp-def-term"
                          style={
                            it.color
                              ? {
                                  background: `${it.color}1f`,
                                  color: it.color,
                                  border: `1px solid ${it.color}55`,
                                }
                              : undefined
                          }
                        >
                          {it.term}
                        </span>
                        <span className="hlp-def-txt" dangerouslySetInnerHTML={raw(it.def)} />
                      </div>
                    ))}
                  </div>
                  {sec.note ? <p className="hlp-note" dangerouslySetInnerHTML={raw(sec.note)} /> : null}
                </>
              ) : (
                <div className="hlp-sec-html" dangerouslySetInnerHTML={raw(sec.html ?? '')} />
              )}
            </div>
          ))}

          {h.links?.length ? (
            <div className="hlp-links">
              {h.links.map((l) => (
                <a key={l.href} href={l.href} className="hlp-link">
                  {l.label}
                </a>
              ))}
            </div>
          ) : null}
        </div>
      </aside>
    </>
  );
}
